/**
 * Canonical secret + path scrub for terminal/log content surfaced to humans.
 *
 * This is the ONE source of truth for the secret-pattern set. It backs the
 * platform-admin live pane view and the admin-debug `tail-logs` pane read
 * (both in `@augmented/api`), so an IL-staff operator gets the same scrub
 * regardless of which surface they read the pane through - no asymmetric
 * "redacted here, raw there" false assurance (ENG-6588 council review).
 *
 * Security posture: redaction is BEST-EFFORT, not a guarantee. It catches the
 * token shapes this platform mints or commonly handles; it cannot catch a
 * password typed at a prompt, a custom key format, or PII in message content.
 * The ACCESS GATE (platform-admin email domain) is the primary control. Keep
 * that framing - do not let "we redact" stand in for "we decided who may see
 * customer terminals".
 *
 * NOTE: `@integrity-labs/augmented-mcp` carries its own copy of this pattern
 * set in `pane-tail.ts` (the host-side `/investigate` path) because that bundle
 * has no `@augmented/core` dependency by design. The two MUST stay in sync;
 * this module is the canonical list.
 */

/** A secret pattern + the label its match is replaced with. */
export interface SecretPattern {
  re: RegExp;
  label: string;
}

/**
 * Token shapes redacted from any human-facing terminal/log frame. Each regex is
 * global so every occurrence in a frame is replaced.
 */
export const SECRET_PATTERNS: readonly SecretPattern[] = [
  // Augmented host API keys
  { re: /tlk_[A-Za-z0-9]{8,}/g, label: 'agt-api-key' },
  // Slack tokens: bot/app/user/refresh/config
  { re: /x(?:ox[abprs]|app)-[A-Za-z0-9-]{8,}/g, label: 'slack-token' },
  // JWTs (three base64url segments, first always starts with eyJ)
  { re: /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, label: 'jwt' },
  // OpenAI / Anthropic-style keys (sk-..., sk-ant-...)
  { re: /sk-[A-Za-z0-9_-]{16,}/g, label: 'sk-key' },
  // AWS access key IDs
  { re: /(?:AKIA|ASIA)[0-9A-Z]{16}/g, label: 'aws-key' },
  // GitHub tokens
  { re: /gh[pousr]_[A-Za-z0-9]{20,}/g, label: 'github-token' },
  // Telegram bot tokens: <8-10 digit bot id>:<35-char secret> (ENG-9353).
  { re: /[0-9]{8,10}:[A-Za-z0-9_-]{35}/g, label: 'telegram-bot-token' },
  // Bearer authorization tokens (opaque or JWT). The 16+ length floor keeps
  // ordinary "Bearer authentication" prose intact — real bearer tokens are far
  // longer than any common word. `[ \t]+` (not `\s+`) so the match cannot span a
  // newline and swallow the line break, changing the log's line structure
  // (ENG-9353).
  { re: /[Bb]earer[ \t]+[A-Za-z0-9._-]{16,}/g, label: 'bearer-token' },
];

/** Replace every known secret shape in `text` with `<redacted:label>`. */
export function redactSecrets(text: string): string {
  let out = text;
  for (const { re, label } of SECRET_PATTERNS) {
    out = out.replace(re, `<redacted:${label}>`);
  }
  return out;
}

/**
 * Host-agnostic `.augmented` path scrub. Unlike the mcp helper
 * (`redactAugmentedPaths`, anchored on the *local* homedir), this matches an
 * `.augmented` path under ANY parent - because the content is captured on a
 * remote agent host (`/root/.augmented/...`) and redacted in the control-plane
 * API, where the local homedir bears no relation to the host's. The character
 * class stays bounded to non-whitespace / non-quote chars so the regex is
 * linear (no catastrophic backtracking).
 */
export function redactAgentPaths(text: string): string {
  return text.replace(
    // An optional leading path (drive / root / segments) up to `.augmented`,
    // then the `.augmented` segment and any trailing path. Accept `/` and `\`.
    /(?:[A-Za-z]:)?(?:[\\/][^\s'"`\\/]*)*[\\/]\.augmented(?:[\\/][^\s'"`]*)*/g,
    '<augmented-path>',
  );
}

/**
 * Full scrub for a pane/log frame surfaced to a human: strip `.augmented`
 * paths first, then known secret shapes. Order matters only cosmetically.
 */
export function redactPaneFrame(text: string): string {
  return redactSecrets(redactAgentPaths(text));
}

/**
 * Strip ANSI/VT escape sequences and control bytes from raw terminal output so
 * it reads as plain text (ENG-7400).
 *
 * `tmux capture-pane -p` already renders the screen as plain text, but the
 * pane.log fallback (the `pipe-pane` sink) is the RAW pty stream: SGR colours,
 * cursor addressing (`ESC[23;1H`), erase codes (`ESC[K`), cursor show/hide
 * (`ESC[?25h/l`), OSC window titles, and same-line CR redraws. Rendered
 * verbatim those make the frame unreadable.
 *
 * This is a strip, not a terminal emulation: cursor-addressed redraws are kept
 * as sequential lines rather than replayed onto a screen grid, so spinner-heavy
 * output reads as repeated lines. Readable, not pixel-faithful.
 */
export function sanitizeTerminalFrame(text: string): string {
  return (
    text
      // The host capture applies `tail -c <cap>`, which can cut mid-sequence
      // and leave the first sequence without its ESC byte (e.g. a leading
      // `[2C`). One bounded strip at position 0 only; requires at least one
      // parameter char so bracketed prose like `[drain]` is never touched.
      .replace(/^\[[0-9;?]+[A-Za-z]/, '')
      // String-type sequences (OSC / DCS / PM / APC): consume to BEL or ST,
      // or to end-of-frame when the terminator was truncated away.
      // eslint-disable-next-line no-control-regex
      .replace(/\x1b[\]P^_][\s\S]*?(?:\x07|\x1b\\|$)/g, '')
      // CSI: ESC [ params (0x30-0x3F) intermediates (0x20-0x2F) final (0x40-0x7E).
      // Also consumes a sequence the byte cap truncated before its final byte
      // (end-of-frame alternative), so a cut `ESC[3` never leaks a stray `3`.
      // eslint-disable-next-line no-control-regex
      .replace(/\x1b\[[0-?]*[ -/]*(?:[@-~]|$)/g, '')
      // Remaining short escapes (ESC 7/8, ESC =, charset selects like ESC ( B)
      // eslint-disable-next-line no-control-regex
      .replace(/\x1b[ -/]*[0-~]/g, '')
      // Any stray ESC left over (e.g. truncated at end of frame)
      .replace(/\x1b/g, '')
      // A raw pty uses CR for same-line redraw; keep each pass on its own line
      .replace(/\r\n?/g, '\n')
      // Drop remaining C0 control bytes (BEL, BS, ...) and DEL; keep \n and \t
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, '')
  );
}
